import React from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { Menu, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "./ui/sheet";
import { useBrand, brandLogo, useInstitutional } from "../lib/brand";

const NAV = [
  { to: "/", label: "Início" },
  { to: "/casas", label: "Casas" },
  { to: "/sobre", label: "Sobre" },
  { to: "/contato", label: "Contato" },
];

function formatBrPhone(digits) {
  if (!digits) return "";
  const d = String(digits).replace(/\D/g, "");
  // 55 + DDD (2) + 5-digit + 4-digit  = 13 digits
  if (d.length === 13 && d.startsWith("55")) {
    return `+55 (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  }
  return `+${d}`;
}

function formatCnpj(v) {
  if (!v) return "";
  const d = String(v).replace(/\D/g, "");
  if (d.length !== 14) return v;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

export default function PublicShell({ children }) {
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const brand = useBrand();
  const inst = useInstitutional();
  const logoPrimary = brandLogo(brand, "primary");
  const logoLight = brandLogo(brand, "light");

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F4EF]">
      {/* Skip-to-content — visível apenas no foco por teclado (WCAG 2.4.1) */}
      <a
        href="#conteudo-principal"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:rounded-md focus:bg-[#0AA89C] focus:text-white focus:shadow-lg focus:outline-none"
        data-testid="skip-to-content"
      >
        Pular para o conteúdo
      </a>
      <header
        data-testid="public-header"
        className={`sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-white/40 ${isHome ? "" : "shadow-[0_1px_0_rgba(0,0,0,0.04)]"}`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 md:h-[88px] flex items-center justify-between gap-6">
          <Link
            to="/"
            data-testid="brand-link"
            aria-label="Azuhomes — ir para o início"
            className="flex items-center gap-3 shrink-0 py-2"
          >
            {logoPrimary ? (
              <img
                src={logoPrimary}
                alt="Azuhomes"
                className="h-11 md:h-14 lg:h-16 w-auto select-none"
              />
            ) : (
              <>
                <span className="font-display text-2xl md:text-3xl lg:text-[32px] tracking-tight text-[#0AA89C] leading-none">
                  Azuhomes
                </span>
                <span className="hidden sm:inline text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-[#066B63] mt-1">
                  Casas de férias
                </span>
              </>
            )}
          </Link>

          <nav className="hidden md:flex items-center gap-7 lg:gap-9" aria-label="Navegação principal">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-[15px] tracking-wide transition-colors ${
                    isActive ? "text-[#0AA89C] font-semibold" : "text-slate-600 hover:text-[#0AA89C]"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/admin/login"
              data-testid="admin-link"
              className="text-xs uppercase tracking-[0.24em] text-slate-500 hover:text-[#0AA89C] pl-4 ml-1 border-l border-slate-200"
            >
              Painel
            </Link>
          </nav>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  data-testid="mobile-menu-btn"
                  variant="ghost"
                  size="icon"
                  aria-label="Abrir menu"
                  className="h-11 w-11"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-white">
                <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
                <div className="mt-8 flex flex-col gap-4">
                  {NAV.map((n) => (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                      className="text-lg text-slate-700 hover:text-[#0AA89C] transition-colors"
                    >
                      {n.label}
                    </Link>
                  ))}
                  <Link
                    to="/admin/login"
                    data-testid="nav-mobile-admin"
                    className="text-base text-slate-500 mt-6 pt-4 border-t border-slate-100 hover:text-[#0AA89C]"
                  >
                    Painel administrativo
                  </Link>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main id="conteudo-principal" tabIndex={-1} className="flex-1 focus:outline-none">{children}</main>

      <footer className="mt-24 bg-[#22302E] text-white/75">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-3 gap-10">
          <div>
            {logoLight ? (
              <img
                src={logoLight}
                alt="Azuhomes"
                className="h-12 md:h-14 w-auto select-none"
              />
            ) : (
              <div className="font-display text-3xl md:text-4xl text-white leading-none">Azuhomes</div>
            )}
            <p className="mt-5 text-sm text-white/70 leading-relaxed max-w-sm">
              Administração de casas de férias em Pernambuco, com atendimento direto e sem intermediários.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#3B9FD4] mb-4">Institucional</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to="/sobre" className="hover:text-white">Sobre a Azuhomes</Link></li>
              <li><Link to="/casas" className="hover:text-white">Nossas casas</Link></li>
              <li><Link to="/contato" className="hover:text-white">Contato</Link></li>
              <li><Link to="/reserva-segura" className="hover:text-white" data-testid="footer-reserva-segura">Reserva Segura</Link></li>
              <li><Link to="/politica-privacidade" className="hover:text-white">Política de privacidade</Link></li>
              <li><Link to="/termos" className="hover:text-white">Termos de uso</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.28em] text-[#3B9FD4] mb-4">Fale conosco</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li>WhatsApp: <a data-testid="footer-whatsapp" href={`https://wa.me/${(inst?.whatsapp || brand?.contact_whatsapp || "").replace(/\D/g, "")}`} className="hover:text-white">{formatBrPhone(inst?.whatsapp || brand?.contact_whatsapp)}</a></li>
              <li>E-mail: <a href={`mailto:${inst?.email_reservations || inst?.email_institutional || brand?.contact_email || ""}`} className="hover:text-white">{inst?.email_reservations || inst?.email_institutional || brand?.contact_email}</a></li>
              {inst?.business_hours && <li className="text-white/60">{inst.business_hours}</li>}
            </ul>
            {(inst?.social?.instagram || inst?.social?.facebook || inst?.social?.linkedin || inst?.social?.youtube) && (
              <div className="mt-4 flex items-center gap-3">
                {inst?.social?.instagram && <a href={inst.social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-white/70 hover:text-white" data-testid="footer-social-instagram"><Instagram className="w-4 h-4" /></a>}
                {inst?.social?.facebook && <a href={inst.social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-white/70 hover:text-white"><Facebook className="w-4 h-4" /></a>}
                {inst?.social?.linkedin && <a href={inst.social.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className="text-white/70 hover:text-white"><Linkedin className="w-4 h-4" /></a>}
                {inst?.social?.youtube && <a href={inst.social.youtube} target="_blank" rel="noreferrer" aria-label="YouTube" className="text-white/70 hover:text-white"><Youtube className="w-4 h-4" /></a>}
              </div>
            )}
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 text-xs text-white/50 flex flex-wrap items-center justify-between gap-2">
            <span>
              © {new Date().getFullYear()} {inst?.trade_name || "Azuhomes"}. Todos os direitos reservados.
              {inst?.legal_name && ` — ${inst.legal_name}`}
              {inst?.address?.city && ` · ${inst.address.city}${inst.address.state ? "/" + inst.address.state : ""}`}
            </span>
            {inst?.cnpj && <span data-testid="footer-cnpj">CNPJ {formatCnpj(inst.cnpj)}</span>}
          </div>
        </div>
      </footer>
    </div>
  );
}
